/**
 * Customer Voices (/customer-voices/, post 57296)
 * ------
 * Behaviour for the page's areoi/core-block markup. Every figure and quote is an
 * ordinary block an editor types into; this reads them back and builds the
 * interactive layer, so nothing here hard-codes copy:
 *
 * - `.cv-tabs`     each `.cv-tabs__panel`'s `.cv-tabs__tab` becomes a tab in a real
 *                  tablist. Without JS every panel simply shows in sequence.
 * - `.cv-ring`     draws an SVG ring from its own figure ("89%", "9/10").
 * - `.cv-waffle`   100 cells, filled from the `<strong>` percentage.
 * - `.cv-bar`      bar height from its own "17%" / "~92%".
 * - logo strip    reveals the ethisphere/wmec-marquee block (it starts hidden)
 *                  and sizes each logo by visual area, not height.
 * - `.cv-reel`     arrow buttons; cards with a YouTube link get that video's
 *                  thumbnail as their still and play in one shared <dialog>.
 *
 * Final values render first. Only figures still below the fold reset and
 * animate in, and nothing animates under prefers-reduced-motion.
 */
(function () {
  'use strict';

  var root = document.querySelector('.cv-band');
  if (!root) { return; }

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var canAnimate = !reduced && 'IntersectionObserver' in window;

  // Stage colours step along the brand gradient, emerald → navy, one per tab.
  var STAGE = ['#12B67D', '#0F7F6D', '#1C6A77', '#2A5681'];
  var STAGE_GLOW = ['rgba(18,182,125,.22)', 'rgba(15,127,109,.18)', 'rgba(28,106,119,.18)', 'rgba(42,86,129,.18)'];
  var STAGE_TINT = ['#ECFAF5', '#EBF5F3', '#EDF3F4', '#EEF2F5'];
  var STAGE_TINT2 = ['#D8F4EA', '#D9EBE8', '#DBE7E9', '#DDE4EB'];

  var uid = 0;

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) { n.className = cls; }
    if (text != null) { n.textContent = text; }
    return n;
  }

  // "89%" → {n: 89, max: 100}; "9/10" → {n: 9, max: 10}; "~92%" → {n: 92, max: 100}
  function parseFigure(text) {
    var m = /(\d+(?:\.\d+)?)\s*(?:\/\s*(\d+(?:\.\d+)?)|%)/.exec(text || '');
    if (!m) { return null; }
    return { n: parseFloat(m[1]), max: m[2] ? parseFloat(m[2]) : 100 };
  }

  // Run `fn(progress)` from 0→1 over `dur` ms once `target` scrolls into view.
  // Figures already on screen (or with animation off) just get progress 1.
  function animateIn(target, fn, dur) {
    if (!canAnimate || target.getBoundingClientRect().top < window.innerHeight) {
      fn(1);
      return;
    }
    fn(0);
    var io = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) { return; }
      io.disconnect();
      var t0 = performance.now();
      (function tick(now) {
        var x = Math.min(1, (now - t0) / (dur || 1400));
        fn(1 - Math.pow(1 - x, 3));
        if (x < 1) { requestAnimationFrame(tick); }
      })(t0);
    }, { threshold: 0.35 });
    io.observe(target);
  }

  // ── Tabs ─────────────────────────────────────────────────────────────────────
  function initTabs(box) {
    var panels = Array.prototype.slice.call(box.querySelectorAll(':scope > .cv-tabs__panel'));
    if (panels.length < 2) { return; }
    var isStages = box.classList.contains('cv-tabs--stages');
    var id = 'cv-tabs-' + (++uid);

    var list = el('div', 'cv-tablist');
    list.setAttribute('role', 'tablist');
    list.setAttribute('aria-label', isStages ? 'Program maturity' : 'Program pillar');

    var track, fill;
    if (isStages) {
      track = el('span', 'cv-tablist__track');
      fill = el('span', 'cv-tablist__fill');
      track.setAttribute('aria-hidden', 'true');
      fill.setAttribute('aria-hidden', 'true');
      list.appendChild(track);
      list.appendChild(fill);
    }

    var tabs = panels.map(function (panel, i) {
      var src = panel.querySelector('.cv-tabs__tab');
      var label = src ? src.querySelector('.cv-tabs__label') : null;
      var icon = src ? src.querySelector('i') : null;

      var tab = el('button', 'cv-tab');
      tab.type = 'button';
      tab.id = id + '-tab-' + i;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', id + '-panel-' + i);

      var ic = el('span', 'cv-tab__icon');
      if (icon) { ic.appendChild(icon.cloneNode(true)); }
      tab.appendChild(ic);

      var text = el('span', 'cv-tab__text');
      text.appendChild(el('span', 'cv-tab__label', label ? label.textContent : 'Tab ' + (i + 1)));
      if (!isStages) {
        var n = panel.querySelectorAll('.cv-quote').length;
        text.appendChild(el('span', 'cv-tab__count', n + (n === 1 ? ' leader' : ' leaders')));
      }
      tab.appendChild(text);

      if (!isStages) {
        var check = el('span', 'cv-tab__check');
        check.setAttribute('aria-hidden', 'true');
        tab.appendChild(check);
      } else {
        tab.style.setProperty('--cv-c', STAGE[i % STAGE.length]);
        tab.style.setProperty('--cv-glow', STAGE_GLOW[i % STAGE.length]);
      }

      panel.id = id + '-panel-' + i;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      panel.setAttribute('tabindex', '0');

      tab.addEventListener('click', function () { select(i); });
      tab.addEventListener('keydown', function (e) {
        var d = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
        var to = e.key === 'Home' ? 0 : e.key === 'End' ? panels.length - 1 : null;
        if (d) { to = (i + d + panels.length) % panels.length; }
        if (to == null) { return; }
        e.preventDefault();
        select(to);
        tabs[to].focus();
      });

      list.appendChild(tab);
      return tab;
    });

    function select(n) {
      tabs.forEach(function (t, i) {
        var on = i === n;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        t.classList.toggle('is-on', on);
        t.classList.toggle('is-reached', i <= n);
        panels[i].hidden = !on;
      });
      if (isStages) {
        var c = STAGE[n % STAGE.length];
        box.style.setProperty('--cv-stage', c);
        box.style.setProperty('--cv-tint', STAGE_TINT[n % STAGE.length]);
        box.style.setProperty('--cv-tint2', STAGE_TINT2[n % STAGE.length]);
        fill.style.width = (n / (panels.length - 1) * (100 - 100 / panels.length)) + '%';
      }
      // Restart the panel's entrance animation on every switch.
      panels[n].classList.remove('is-entering');
      void panels[n].offsetWidth;
      panels[n].classList.add('is-entering');
    }

    if (isStages) {
      // Track runs from the first icon's centre to the last's.
      var inset = (50 / panels.length) + '%';
      track.style.left = track.style.right = fill.style.left = inset;
    }

    box.insertBefore(list, box.firstChild);
    box.classList.add('is-enhanced');
    select(0);
  }

  // ── Rings ────────────────────────────────────────────────────────────────────
  var SVG = 'http://www.w3.org/2000/svg';
  var C = 2 * Math.PI * 42;

  function initRings(wrap) {
    var rings = wrap.querySelectorAll('.cv-ring');
    if (!rings.length) { return; }

    var defs = document.createElementNS(SVG, 'svg');
    defs.setAttribute('width', '0');
    defs.setAttribute('height', '0');
    defs.setAttribute('aria-hidden', 'true');
    defs.style.position = 'absolute';
    defs.innerHTML = '<defs><linearGradient id="cvRingGrad" x1="0" y1="0" x2="1" y2="1">' +
      '<stop offset="0" stop-color="#2A5681"/><stop offset="1" stop-color="#12B67D"/></linearGradient></defs>';
    wrap.appendChild(defs);

    var updates = [];
    Array.prototype.forEach.call(rings, function (ring) {
      var valueEl = ring.querySelector('.cv-ring__value');
      if (!valueEl) { return; }
      var original = valueEl.textContent.trim();
      var fig = parseFigure(original);
      if (!fig) { return; }

      var dial = el('div', 'cv-ring__dial');
      var svg = document.createElementNS(SVG, 'svg');
      svg.setAttribute('viewBox', '0 0 100 100');
      svg.setAttribute('aria-hidden', 'true');
      svg.innerHTML = '<circle class="cv-ring__track" cx="50" cy="50" r="42"/>' +
        '<circle class="cv-ring__arc" cx="50" cy="50" r="42" transform="rotate(-90 50 50)"/>';
      var arc = svg.querySelector('.cv-ring__arc');
      dial.appendChild(svg);
      ring.insertBefore(dial, valueEl);
      dial.appendChild(valueEl);

      // Keep the editor's own wording ("9/10", "89%") as the accessible text and
      // only count the number inside it.
      var numText = /(\d+(?:\.\d+)?)/.exec(original)[1];
      var decimals = numText.indexOf('.') > -1 ? numText.split('.')[1].length : 0;
      updates.push(function (p) {
        arc.style.strokeDasharray = (fig.n / fig.max * C * p).toFixed(1) + ' ' + C.toFixed(1);
        arc.style.opacity = p > 0 ? 1 : 0;
        valueEl.textContent = p >= 1 ? original : original.replace(numText, (fig.n * p).toFixed(decimals));
      });
    });

    animateIn(wrap, function (p) { updates.forEach(function (u) { u(p); }); });
  }

  // ── Logo marquee ─────────────────────────────────────────────────────────────
  // The ethisphere/wmec-marquee block renders with `[data-wmec-reveal]`, which
  // starts at opacity 0 and waits for a page script to add `.is-in` (on the
  // WMEC page that's a per-page IntersectionObserver). The strip sits just
  // under the fold here, so it is simply shown.
  function initLogoReveal(node) { node.classList.add('is-in'); }

  // Equal visual weight, not equal height. The logos are tightly cropped files
  // (no padding inside them), so an image's own aspect ratio is the mark's: a long wordmark (Colgate, ~8:1) at the same height as a stacked
  // emblem (Avangrid, ~0.7:1) looks twelve times heavier. Giving each logo the
  // same area — wider-and-shorter for wordmarks, narrower-and-taller for
  // emblems — then capping both dimensions evens the strip out. Works for any
  // logo an editor swaps in, as long as it's tightly cropped.
  var LOGO_AREA = 4000, LOGO_MAX_H = 56, LOGO_MAX_W = 168;

  function sizeLogo(img) {
    function apply() {
      var r = img.naturalWidth / img.naturalHeight;
      if (!r || !isFinite(r)) { return; }
      var w = Math.sqrt(LOGO_AREA * r), h = w / r;
      if (h > LOGO_MAX_H) { h = LOGO_MAX_H; w = h * r; }
      if (w > LOGO_MAX_W) { w = LOGO_MAX_W; h = w / r; }
      img.style.width = Math.round(w) + 'px';
      img.style.height = Math.round(h) + 'px';
    }
    if (img.complete && img.naturalWidth) { apply(); } else { img.addEventListener('load', apply); }
  }

  // ── Waffle + bars ────────────────────────────────────────────────────────────
  function initWaffle(box) {
    var n = box.querySelector('.cv-waffle__n strong');
    var fig = n ? parseFigure(n.textContent) : null;
    if (!fig) { return; }
    var pct = Math.round(fig.n / fig.max * 100);
    var grid = el('div', 'cv-waffle__grid');
    grid.setAttribute('aria-hidden', 'true');
    var cells = [];
    for (var i = 0; i < 100; i++) { cells.push(grid.appendChild(el('span'))); }
    box.appendChild(grid);
    var original = n.textContent;
    animateIn(box, function (p) {
      var k = Math.round(pct * p);
      cells.forEach(function (c, i) { c.classList.toggle('is-on', i < k); });
      n.textContent = p >= 1 ? original : k + '%';
    });
  }

  function initBars(box) {
    var bars = box.querySelectorAll('.cv-bar');
    var sets = [];
    Array.prototype.forEach.call(bars, function (bar) {
      var v = bar.querySelector('.cv-bar__v');
      var fig = v ? parseFigure(v.textContent) : null;
      if (!fig) { return; }
      var col = el('span', 'cv-bar__col');
      col.setAttribute('aria-hidden', 'true');
      bar.insertBefore(col, v.nextSibling);
      sets.push(function (p) { col.style.height = (fig.n / fig.max * 85 * p) + 'px'; });
    });
    animateIn(box, function (p) { sets.forEach(function (s) { s(p); }); });
  }

  // ── Reel + quote dialog ──────────────────────────────────────────────────────
  function youtubeId(url) {
    var m = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/.exec(url || '');
    return m ? m[1] : null;
  }

  // Click-to-play only: nothing from YouTube loads until the visitor opens a
  // card, and then from the no-cookie domain (same as the site's video-poster).
  function youtubeEmbed(id) {
    return 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0';
  }

  // The card's still is the video's own thumbnail, so the face on the card is
  // always the person in the interview. i.ytimg.com is a static image host (no
  // cookies). maxres doesn't exist for every upload — YouTube then serves a
  // 120×90 placeholder, so fall back to hq when the image comes back that small.
  function addPoster(card, id) {
    var img = el('img', 'cv-voice__poster');
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.addEventListener('load', function () {
      if (img.naturalWidth <= 120 && img.src.indexOf('maxresdefault') > -1) {
        img.src = 'https://i.ytimg.com/vi/' + id + '/hqdefault.jpg';
      }
    });
    img.addEventListener('error', function () { img.remove(); });
    img.src = 'https://i.ytimg.com/vi/' + id + '/maxresdefault.jpg';
    card.insertBefore(img, card.firstChild);
  }

  function initReel(reel) {
    var cards = reel.querySelectorAll('.cv-voice');
    if (!cards.length) { return; }

    // Arrows go beside the band's heading stack, as in the design.
    var head = document.querySelector('.cv-voices__head');
    if (head) {
      var nav = el('div', 'cv-reel__nav');
      [['Scroll left', -1, 'fa-arrow-left'], ['Scroll right', 1, 'fa-arrow-right']].forEach(function (a) {
        var b = el('button', 'cv-reel__arrow');
        b.type = 'button';
        b.setAttribute('aria-label', a[0]);
        b.innerHTML = '<i class="fa-solid ' + a[2] + '" aria-hidden="true"></i>';
        b.addEventListener('click', function () {
          reel.scrollBy({ left: a[1] * 300, behavior: reduced ? 'auto' : 'smooth' });
        });
        nav.appendChild(b);
      });
      head.parentNode.insertBefore(nav, head.nextSibling);
      head.parentNode.classList.add('cv-voices__bar');
    }

    var dialog = el('dialog', 'cv-modal');
    dialog.setAttribute('aria-label', 'Customer interview');
    dialog.innerHTML =
      '<div class="cv-modal__box">' +
      '<div class="cv-modal__media" hidden></div>' +
      // The close button lives in the quote panel, not over the player, where
      // it would sit on YouTube's own top-right controls.
      '<figure class="cv-modal__body">' +
      '<button type="button" class="cv-modal__close" aria-label="Close"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>' +
      '<blockquote class="cv-modal__quote"></blockquote>' +
      '<figcaption class="cv-modal__cite"></figcaption></figure>' +
      '</div>';
    document.body.appendChild(dialog);
    var media = dialog.querySelector('.cv-modal__media');

    function close() {
      media.innerHTML = '';
      media.hidden = true;
      if (dialog.open) { dialog.close(); }
    }
    dialog.querySelector('.cv-modal__close').addEventListener('click', close);
    // A click on the backdrop lands on the <dialog> itself, not on its box.
    dialog.addEventListener('click', function (e) { if (e.target === dialog) { close(); } });
    // Esc closes natively; this clears the player so audio stops with it.
    dialog.addEventListener('close', function () { media.innerHTML = ''; media.hidden = true; });

    Array.prototype.forEach.call(cards, function (card) {
      var who = card.querySelector('.cv-voice__who');
      var nameEl = who ? who.querySelector('strong') : null;
      var name = nameEl ? nameEl.textContent.trim() : '';
      var company = who ? who.textContent.replace(name, '').trim() : '';
      var role = card.querySelector('.cv-voice__role');
      var quote = card.querySelector('.cv-voice__quote');
      var link = card.querySelector('.cv-voice__video a');
      var vid = link ? youtubeId(link.getAttribute('href')) : null;
      var embed = vid ? youtubeEmbed(vid) : null;

      if (vid) {
        card.classList.add('has-video');
        addPoster(card, vid);
      }

      var open = el('button', 'cv-voice__open');
      open.type = 'button';
      open.setAttribute('aria-label', (embed ? 'Watch ' : 'Read ') + name + (company ? ', ' + company : ''));
      open.setAttribute('aria-haspopup', 'dialog');
      card.appendChild(open);

      open.addEventListener('click', function () {
        dialog.querySelector('.cv-modal__quote').textContent = quote ? quote.textContent : '';
        var cite = dialog.querySelector('.cv-modal__cite');
        cite.innerHTML = '';
        cite.appendChild(el('strong', null, name));
        cite.appendChild(document.createTextNode(
          [role ? role.textContent.trim() : '', company].filter(Boolean).map(function (s) { return ', ' + s; }).join('')));
        if (embed) {
          var f = document.createElement('iframe');
          f.src = embed;
          f.title = 'Interview with ' + name;
          f.allow = 'autoplay; encrypted-media; picture-in-picture';
          f.allowFullscreen = true;
          media.appendChild(f);
          media.hidden = false;
        }
        dialog.showModal();
        dialog.querySelector('.cv-modal__close').focus();
      });
    });
  }

  // ── Quote initials (pillar cards) ────────────────────────────────────────────
  function initInitials(quote) {
    var who = quote.querySelector('.cv-quote__who');
    var name = who ? who.querySelector('strong') : null;
    if (!name) { return; }
    var ini = name.textContent.split(/\s+/).filter(Boolean).map(function (w) { return w[0]; }).slice(0, 2).join('');
    var badge = el('span', 'cv-quote__ini', ini);
    badge.setAttribute('aria-hidden', 'true');
    // Name + role move into one box beside the badge, keeping their <br>.
    var id = el('span', 'cv-quote__id');
    while (who.firstChild) { id.appendChild(who.firstChild); }
    who.appendChild(badge);
    who.appendChild(id);
    who.classList.add('has-ini');
  }

  function each(sel, fn) { Array.prototype.forEach.call(document.querySelectorAll(sel), fn); }

  each('.cv-tabs', initTabs);
  each('.cv-rings', initRings);
  each('.cv-band [data-wmec-reveal]', initLogoReveal);
  each('.cv-logos .wmec-marquee-item img', sizeLogo);
  each('.cv-waffle', initWaffle);
  each('.cv-bars', initBars);
  each('.cv-reel', initReel);
  each('.cv-quote', initInitials);
})();
